import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import {
  fetchStatesAsync,
  selectAllStates,
  selectStatesLoading,
  selectStatesError,
} from "../../../Slices/statesSlice";
import {
  isValidEmail,
  isValidPin,
  isValidPhoneNumber,
} from "../../../Utils/validFormDetails";
import {
  addSchoolAdmin,
  isAuthenticated,
} from "../../../ApiService/ApiService";
import { useSelector, useDispatch } from "react-redux";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddSchoolAdmin({ setActionComponent, currentSchoolId }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStatesAsync());
  }, [dispatch]);
  const states = useSelector(selectAllStates);
  const statesLoading = useSelector(selectStatesLoading);
  const statesError = useSelector(selectStatesError);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    schoolId: currentSchoolId,
    street1: "",
    street2: "",
    city: "",
    stateId: "",
    pin: "",
    phone: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.street1 ||
      !formData.schoolId ||
      !formData.city ||
      !formData.stateId ||
      !formData.pin ||
      !formData.phone
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill all the details",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidEmail(formData.email)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid email address",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (!isValidPin(formData.pin)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 6-digit pin",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidPhoneNumber(formData.phone)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 10-digit number",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Passwords don't match",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addSchoolAdmin(formData);
        // console.log("RES", response);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "School admin added",
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
            setFormData({
              firstName: "",
              middleName: "",
              lastName: "",
              email: "",
              password: "",
              confirmPassword: "",
              schoolId: currentSchoolId,
              street1: "",
              street2: "",
              city: "",
              stateId: "",
              pin: "",
              phone: "",
            });
            setActionComponent(null);
          } else {
            const errorMessage = response?.data?.errorMessage;
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server.",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
        return;
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Failed to add school admin",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ p: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add School Admin
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street 1"
                  name="street1"
                  value={formData.street1}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street 2"
                  name="street2"
                  value={formData.street2}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="state">State</InputLabel>
                  <Select
                    name="stateId"
                    label="State"
                    labelId="state"
                    value={formData.stateId}
                    onChange={handleChange}
                    size="small"
                    disabled={statesLoading || statesError}
                  >
                    {statesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : statesError ? (
                      <MenuItem value="">Error fetching states</MenuItem>
                    ) : (
                      states.map((state) => (
                        <MenuItem key={state?.id} value={state?.id}>
                          {state?.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setActionComponent(null)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

AddSchoolAdmin.propTypes = {
  setActionComponent: PropTypes.func.isRequired,
  currentSchoolId: PropTypes.number.isRequired,
};

export default AddSchoolAdmin;
